import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Upload, Star, MessageCircle, StickyNote, Bell, Award, Settings, Shield, LogOut, Send, X, Heart, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import useModalHistory from '../hooks/useModalHistory';

const ProfilePage = () => {
  const { user, logout, token } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const navigate = useNavigate();
  
  // Handle browser back button for modal
  const { closeModal: closeFeedbackModal } = useModalHistory(showFeedbackModal, () => setShowFeedbackModal(false), 'feedback-modal');

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(res.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.error('Please enter your feedback');
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/users/feedback`, {
        message: feedbackMessage,
        category: 'general'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feedback submitted! Thank you for helping us improve.');
      closeFeedbackModal();
      setFeedbackMessage('');
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadNotifications(res.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      checkAdmin();
    }
  }, [token]);

  const handleProfilePicUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/profile/upload-picture`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile picture updated!');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to upload picture');
    }
  };

  if (!token || !user) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] pb-24 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="mb-6 flex justify-center">
            <div className="w-32 h-32 rounded-full bg-white/5 flex items-center justify-center border-2 border-white/10">
              <User size={64} className="text-white/30" />
            </div>
          </div>
          <h2 className="playfair text-2xl text-white mb-6">Sign in to view profile</h2>
          <Button
            data-testid="profile-signin-button"
            onClick={() => navigate('/auth')}
            className="bg-gradient-to-br from-amber-600 to-amber-800 text-white px-12 py-6 rounded-full text-lg"
          >
            Sign In
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      <div className="max-w-md mx-auto p-4">
        {/* Profile Header */}
        <div className="flex flex-col items-center mb-8">
          <label className="relative cursor-pointer group">
            <input
              data-testid="profile-pic-input"
              type="file"
              accept="image/*"
              onChange={(e) => handleProfilePicUpload(e.target.files[0])}
              className="hidden"
            />
            <div className="w-24 h-24 rounded-full overflow-hidden bg-white/5 flex items-center justify-center border-2 border-amber-500 group-hover:border-amber-400 transition-colors">
              {user.profile_pic ? (
                <img src={user.profile_pic} alt={user.username} className="w-full h-full object-cover" />
              ) : (
                <User size={48} className="text-white/50" />
              )}
            </div>
            <div className="absolute bottom-0 right-0 bg-amber-600 rounded-full p-1">
              <Upload size={16} className="text-white" />
            </div>
          </label>
          <h1 className="playfair text-2xl text-white font-bold mt-4">{user.username}</h1>
          <p className="text-white/50 text-sm">{user.email}</p>
        </div>

        {/* Settings */}
        <div className="space-y-3 mb-6">
          <button 
            onClick={() => navigate('/notifications')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2 relative">
              <Bell className="text-amber-500" size={20} />
              Notifications
              {unreadNotifications > 0 && (
                <span className="bg-amber-500 text-black text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {unreadNotifications}
                </span>
              )}
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            data-testid="my-favorites-button"
            onClick={() => navigate('/favorites')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Heart className="text-amber-500" size={20} />
              My Favorites
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            data-testid="my-smoked-button"
            onClick={() => navigate('/my-smoked')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <CheckCircle className="text-amber-500" size={20} />
              Cigars I've Smoked
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            data-testid="my-ratings-button"
            onClick={() => navigate('/my-ratings')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Star className="text-amber-500" size={20} />
              My Ratings
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            data-testid="my-comments-button"
            onClick={() => navigate('/my-comments')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <MessageCircle className="text-amber-500" size={20} />
              My Comments
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            onClick={() => navigate('/my-notes')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <StickyNote className="text-amber-500" size={20} />
              My Notes
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            onClick={() => navigate('/badges')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Award className="text-amber-500" size={20} />
              My Badges
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            onClick={() => navigate('/settings')}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Settings className="text-amber-500" size={20} />
              Settings
            </span>
            <span className="text-white/50">→</span>
          </button>
          <button
            onClick={() => setShowFeedbackModal(true)}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-3 px-4 hover:bg-white/10 transition-colors flex items-center justify-between"
          >
            <span className="flex items-center gap-2">
              <Send className="text-amber-500" size={20} />
              Submit Feedback
            </span>
            <span className="text-white/50">→</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/admin')}
              className="w-full bg-gradient-to-r from-amber-900/30 to-amber-700/20 border border-amber-500/30 text-white rounded-lg py-3 px-4 hover:bg-amber-900/40 transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Shield className="text-amber-500" size={20} />
                Admin Dashboard
              </span>
              <span className="text-amber-500">→</span>
            </button>
          )}
        </div>

        {/* Sign Out */}
        <Button
          data-testid="sign-out-button"
          onClick={() => {
            logout();
            toast.success('You have been signed out successfully');
            navigate('/auth');
          }}
          variant="outline"
          className="w-full border-red-500 text-red-500 hover:bg-red-500/10 rounded-full py-6 flex items-center justify-center gap-2"
        >
          <LogOut size={20} />
          Sign Out
        </Button>
      </div>
      
      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1c] border border-white/10 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Submit Feedback</h2>
              <button onClick={() => closeFeedbackModal()} className="text-white/50 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <p className="text-white/60 text-sm mb-4">
              Help us improve Cigar Ranker! Your feedback goes directly to the admin.
            </p>
            
            <div className="mb-4">
              <label className="text-white/70 text-sm mb-2 block">Your Feedback</label>
              <textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder="Tell us what you think..."
                rows={4}
                className="w-full bg-white/5 border border-white/10 text-white rounded-lg py-2 px-3 placeholder:text-white/30 resize-none"
              />
            </div>
            
            <Button
              onClick={handleSubmitFeedback}
              disabled={submittingFeedback || !feedbackMessage.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-full disabled:opacity-50"
            >
              {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </div>
      )}
      
      <BottomNav />
    </div>
  );
};

export default ProfilePage;
